import { z } from 'zod';
import { BreBAliasTypeSchema } from '../catalogs/domain-catalogs.js';
import { ParticipantTypeSchema } from '../catalogs/banks.js';
import { BusinessDateSchema, TimestampSchema } from '../common/clock.js';
import { MoneySchema, PositiveMoneySchema } from '../common/money.js';
import { defineStateMachine } from '../common/state-machine.js';
import { CustomerIdSchema, PaymentIntentIdSchema } from '../common/ids.js';
export * from './api.js';
export * from './scenarios.js';
/**
 * Simuladores (`services/sandbox`) y controles de demo (`/v1/demo/*`, solo `DEMO_MODE=true`).
 * Los simuladores se comportan como sistemas externos reales (HTTP, latencia, callbacks firmados,
 * fallas configurables) y se consumen **solo** a través de los puertos.
 */
/** Transferencia Bre-B en el simulador — máquinas de estado §11. */
export const SandboxTransferMachine = defineStateMachine({
    name: 'SandboxBreBTransfer',
    states: ['RECEIVED', 'PROCESSING', 'ACCEPTED', 'REJECTED'],
    initial: ['RECEIVED'],
    terminal: ['ACCEPTED', 'REJECTED'],
    transitions: {
        RECEIVED: ['PROCESSING', 'REJECTED'],
        PROCESSING: ['ACCEPTED', 'REJECTED'],
        ACCEPTED: [],
        REJECTED: [],
    },
});
export const SandboxTransferStatusSchema = SandboxTransferMachine.schema;
export const TransferDirectionSchema = z.enum(['OUTGOING', 'INCOMING']);
/** Latencia por defecto de procesamiento del simulador (ms). */
export const SANDBOX_DEFAULT_LATENCY_MS = { min: 1000, max: 3000 };
export const SandboxParticipantSchema = z.object({
    code: z.string(),
    name: z.string(),
    type: ParticipantTypeSchema,
    callbackUrl: z.url().nullable(),
});
export const SandboxAliasSchema = z.object({
    aliasType: BreBAliasTypeSchema,
    aliasValue: z.string(),
    participantCode: z.string(),
    accountNumber: z.string(),
    accountType: z.string(),
    holderName: z.string(),
    holderDocument: z.string(),
    isMerchant: z.boolean(),
    merchantName: z.string().nullable(),
    mcc: z.string().nullable(),
    city: z.string().nullable(),
    status: z.enum(['ACTIVE', 'INACTIVE']),
});
export const DepositAccountStatusSchema = z
    .enum(['ACTIVE', 'BLOCKED', 'EMBARGOED', 'CLOSED'])
    .meta({ id: 'DepositAccountStatus' });
export const DepositHoldStatusSchema = z.enum(['ACTIVE', 'CONSUMED', 'RELEASED']);
export const PaymentHabitSchema = z.enum([
    'AL_DIA',
    'MORA_30',
    'MORA_60',
    'MORA_90',
    'MORA_120_PLUS',
    'SIN_HISTORIA',
]);
/** `POTENTIAL_MATCH` = `REVIEW` del modelo de datos (requiere revisión humana). */
export const SanctionsResultSchema = z
    .enum(['CLEAR', 'POTENTIAL_MATCH', 'MATCH'])
    .meta({ id: 'SanctionsResult' });
export const ESignEnvelopeStatusSchema = z
    .enum(['SENT', 'SIGNED', 'DECLINED', 'EXPIRED'])
    .meta({ id: 'ESignEnvelopeStatus' });
// ── Reglas de falla ───────────────────────────────────────────────────────
export const FaultTargetSchema = z.enum([
    'BREB_SEND',
    'BREB_CALLBACK',
    'BREB_RESOLVE',
    'DEPOSIT_CREDIT',
    'DEPOSIT_DEBIT',
    'BUREAU',
    'KYC',
    'ESIGN',
]);
export const FaultEffectSchema = z.enum([
    'REJECT',
    'TIMEOUT',
    'DELAY',
    'DUPLICATE_CALLBACK',
    'ERROR_500',
]);
export const FaultRuleSchema = z
    .object({
    id: z.string(),
    target: FaultTargetSchema,
    /** Coincidencia: monto que termina en, llave específica, documento… */
    match: z.object({
        amountEndsWith: z.string().optional(),
        aliasValue: z.string().optional(),
        documentEndsWith: z.string().optional(),
    }),
    effect: FaultEffectSchema,
    params: z.object({
        rejectCode: z.string().optional(),
        delaySeconds: z.int().min(0).optional(),
        timeoutMinutes: z.int().min(0).optional(),
        /** Latencia del callback (ms) para el modo forzado de los Controles de demo. */
        latencyMs: z.int().min(0).max(120_000).optional(),
    }),
    active: z.boolean(),
    remainingUses: z.int().min(0).nullable(),
})
    .meta({ id: 'FaultRule' });
/** `POST /breb/v1/sandbox/faults` (y equivalentes de otros simuladores). */
export const CreateFaultRuleRequestSchema = FaultRuleSchema.omit({ id: true })
    .extend({
    active: z.boolean().default(true),
    remainingUses: z.int().min(0).nullable().default(null),
})
    .meta({ id: 'CreateFaultRuleRequest' });
/**
 * Convenciones determinísticas del sandbox (`01-producto/07-integraciones-externas.md` §11).
 * Los Controles de demo (modo forzado para las próximas N transacciones) tienen prioridad.
 */
export const SANDBOX_MAGIC = {
    breb: {
        /** Centavos del monto → comportamiento de la transferencia. */
        amountCents: {
            '13': 'REJECTED_BY_RECEIVER',
            '99': 'NO_CALLBACK_QUERY_ACCEPTED_60S',
            '98': 'NO_CALLBACK_QUERY_REJECTED_60S',
            '77': 'LATE_CALLBACK_25S',
            '55': 'DUPLICATE_CALLBACK',
        },
        unknownAliases: ['@noexiste', '3000000000'],
    },
    depositCore: {
        /** Centavos `.31` → `creditAndHold` falla (compensación). */
        creditAndHoldFailCents: '31',
    },
    /** Últimos 4 dígitos del documento → central / listas / identidad. */
    documentSuffix: {
        '0000': { bureau: 'THIN_FILE', sanctions: 'MATCH', identity: 'OK' },
        '1111': { bureau: 'LOW_350_DELINQUENT', sanctions: 'CLEAR', identity: 'OK' },
        '2222': { bureau: 'MEDIUM_620', sanctions: 'POTENTIAL_MATCH', identity: 'OK' },
        '3333': { bureau: 'HIGH_780', sanctions: 'CLEAR', identity: 'FACE_MISMATCH' },
    },
    esignDemoOtp: '123456',
};
// ── Controles de demo (`/v1/demo/*`, scope `admin:demo`) ──────────────────
export const DemoClockSchema = z
    .object({
    now: TimestampSchema,
    mode: z.enum(['REAL', 'SIMULATED']),
    offsetDays: z.int(),
    frozen: z.boolean(),
})
    .meta({ id: 'DemoClock' });
export const AdvanceClockRequestSchema = z
    .object({ days: z.int().min(1).max(90) })
    .meta({ id: 'AdvanceClockRequest' });
export const AdvanceClockResponseSchema = z
    .object({
    clock: DemoClockSchema,
    days: z.array(z.object({
        businessDate: BusinessDateSchema,
        interestAccrued: MoneySchema,
        statementsGenerated: z.int(),
        delinquencyChanges: z.int(),
    })),
})
    .meta({ id: 'AdvanceClockResponse' });
export const BreBFaultModeSchema = z.enum([
    'NORMAL',
    'REJECT',
    'TIMEOUT',
    'LATE_CALLBACK',
    'DUPLICATE_CALLBACK',
]);
export const SetBreBFaultModeRequestSchema = z
    .object({
    mode: BreBFaultModeSchema,
    count: z.int().min(1).max(100).default(1),
    latencyMs: z.int().min(0).max(120_000).optional(),
})
    .meta({ id: 'SetBreBFaultModeRequest' });
export const GenerateDemoQrRequestSchema = z
    .object({
    merchantAlias: z.string(),
    amount: PositiveMoneySchema.optional(),
    reference: z.string().max(25).optional(),
})
    .meta({ id: 'GenerateDemoQrRequest' });
export const GenerateDemoQrResponseSchema = z.object({
    payload: z.string(),
    pngDataUrl: z.string(),
});
export const DemoMerchantRefundRequestSchema = z
    .object({ paymentIntentId: PaymentIntentIdSchema, amount: PositiveMoneySchema })
    .meta({ id: 'DemoMerchantRefundRequest' });
export const DemoResetRequestSchema = z
    .object({ scope: z.enum(['ALL', 'CUSTOMER']), customerId: CustomerIdSchema.optional() })
    .refine((v) => v.scope !== 'CUSTOMER' || v.customerId !== undefined, {
    message: 'customerId requerido',
    path: ['customerId'],
})
    .meta({ id: 'DemoResetRequest' });
export const DemoGuideSchema = z.object({
    scripts: z.array(z.object({
        code: z.string(),
        title: z.string(),
        steps: z.array(z.object({ text: z.string(), link: z.string().nullable() })),
    })),
});
//# sourceMappingURL=index.js.map